/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ITheme } from '@fluentui/react/lib/Styling';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { ConfirmationDialog, ConfirmationDialogProps, ModalHeader, ScrollBoundary } from '../../common';
import { AdaptiveCard } from '../../components';
import { ErrorPage } from '../ErrorPage';
import { LoadingPage } from '../LoadingPage';
import { ViewDetailsV2Stage } from './ViewDetailsV2.container';

import './ViewDetailsV2.scss';

export interface ViewDetailsV2ComponentProps {
    stage: ViewDetailsV2Stage;
    stageMessage: string;
    template: any;
    dataPayload: any;
    approvalType: string;
    openUrlHandler: (href: string, id: string, title: string) => void;
    submitHandler: (url: string, data: any) => void;
    confirmationDialogProps?: ConfirmationDialogProps;
    // Correlation id (client request GUID) shown on the error page to aid support diagnosis
    correlationId?: string;
    // Retry callback wired to the error page's action button to re-load the request
    errorAction?: () => void;
}

export const ViewDetailsV2Component: React.FC<ViewDetailsV2ComponentProps> = (
    props: ViewDetailsV2ComponentProps
): JSX.Element => {
    const {
        stage,
        stageMessage,
        approvalType,
        template,
        dataPayload,
        openUrlHandler,
        submitHandler,
        confirmationDialogProps,
        correlationId,
        errorAction,
    } = props;

    const host = getAppHost();
    const loc = host.getLocale();
    const theme: ITheme = React.useContext(CustomizerContext).customizations.settings.theme;
    const themeName = host.getTheme();

    let content: JSX.Element = null;

    if (stage === ViewDetailsV2Stage.DataLoading || stage === ViewDetailsV2Stage.RespondLoading) {
        content = <LoadingPage message={stageMessage} />;
    } else if (stage === ViewDetailsV2Stage.Error) {
        content = <ErrorPage message={stageMessage} correlationId={correlationId} onAction={errorAction} />;
    } else {
        content = (
            <>
                <ScrollBoundary isTopBoundary />
                <AdaptiveCard
                    approvalType={approvalType}
                    themeName={themeName}
                    template={template}
                    dataPayload={dataPayload}
                    openUrlHandler={openUrlHandler}
                    submitHandler={submitHandler}
                />
            </>
        );
    }

    const modalHeader = <ModalHeader subtitle={loc.strings.view_details_subtitle} loc={loc} theme={theme} />;

    return (
        <>
            <div className='view-container' aria-live='polite'>
                <div className='view-details modal-container desktop v2'>
                    {modalHeader}
                    <div className='approval-scrollable-content'>{content}</div>
                </div>
            </div>
            <ConfirmationDialog {...confirmationDialogProps} />
        </>
    );
};
